import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import api from '../../services/api';
import toast from 'react-hot-toast';
import logo from '../../assets/FinalLOGO.png';
import {
  Calendar,
  Clock,
  CheckCircle2,
  Wallet,
  Activity,
  ChevronRight,
  Zap,
  ShieldCheck,
  Stethoscope,
  Users,
  PhoneCall
} from 'lucide-react';

import DashboardHeader from '../../Components/common/DashboardHeader';

const DoctorDashboard = () => {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [availabilityOpen, setAvailabilityOpen] = useState(false);
  const [showAllReviews, setShowAllReviews] = useState(false);
  const [otpInputs, setOtpInputs] = useState({});
  const [verifyingOtp, setVerifyingOtp] = useState(false);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const response = await api.get('/dashboard/doctor');
      setData(response.data.data);
    } catch (error) {
      toast.error('Failed to load clinical data');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOtp = async (surgeryId, otpId, purpose) => {
    const code = otpInputs[surgeryId];
    if (!code || code.length !== 6) {
      toast.error('Please enter a valid 6-digit OTP');
      return;
    }

    setVerifyingOtp(true);
    try {
      const endpoint = purpose === 'surgery_start' || purpose === 'surgery_verification' ? `/surgeries/${surgeryId}/start` : `/surgeries/${surgeryId}/complete`;
      const response = await api.post(endpoint, { otpCode: code, otpId });
      if (response.data.success) {
        toast.success(response.data.message || 'OTP Verified Successfully');
        fetchDashboardData();
        setOtpInputs(prev => ({ ...prev, [surgeryId]: '' }));
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Verification failed');
    } finally {
      setVerifyingOtp(false);
    }
  };

  if (loading || !data) {
    return (
      <div className="p-8 space-y-10 animate-fadeIn bg-[#FDFDFD] min-h-screen">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6">
          <div className="flex items-center gap-6">
            <img src={logo} alt="Loading..." className="h-20 w-auto animate-pulse opacity-50 grayscale" />
            <div className="space-y-4">
              <div className="h-4 w-40 bg-blue-100 animate-pulse rounded" />
              <div className="h-12 w-80 bg-slate-200 animate-pulse rounded-2xl" />
            </div>
          </div>
          <div className="h-20 w-72 bg-white rounded-[28px] animate-pulse shadow-sm border border-slate-100" />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {[1, 2, 3, 4].map(i => <div key={i} className="h-40 bg-white rounded-[32px] animate-pulse border border-slate-100" />)}
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          <div className="lg:col-span-8 h-96 bg-white rounded-[40px] animate-pulse border border-slate-100" />
          <div className="lg:col-span-4 space-y-8">
            <div className="h-64 bg-white rounded-[36px] animate-pulse border border-slate-100" />
            <div className="h-48 bg-blue-600/10 rounded-[36px] animate-pulse border border-blue-100" />
          </div>
        </div>
      </div>
    );
  }

  const { doctor, leadStats, surgeryStats, upcomingSurgeries, recentReviews } = data;
  const surgeriesWithOTP = upcomingSurgeries?.filter(s => s.activeOTP) || [];

  return (
    <div className="p-4 sm:p-6 space-y-8 sm:space-y-10 bg-[#FDFDFD] min-h-screen pb-20">

      <DashboardHeader user={user} />

      {/* Active OTP Verifications Section */}
      {surgeriesWithOTP.length > 0 && (
        <div className="bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 rounded-[32px] p-6 sm:p-8 shadow-md animate-fadeIn">
          <h3 className="text-lg font-black text-amber-800 flex items-center gap-2 mb-6">
            <Zap className="w-5 h-5 text-amber-500" /> Action Required: Active Verifications
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {surgeriesWithOTP.map(surgery => (
              <div key={surgery._id} className="bg-white p-5 rounded-2xl shadow-sm border border-amber-100 flex flex-col gap-4">
                <div className="flex justify-between items-start">
                  <div className="min-w-0 flex-1">
                    <h4 className="font-black text-[#143E78] truncate">{surgery.surgeryName}</h4>
                    <p className="text-xs font-bold text-gray-500 uppercase truncate">{surgery.lead?.patientName}</p>
                  </div>
                  <span className="px-3 py-1 bg-amber-100 text-amber-800 text-[9px] font-black uppercase rounded-full whitespace-nowrap ml-2">
                    {surgery.activeOTP.purpose?.replace(/_/g, ' ')}
                  </span>
                </div>

                <div className="bg-slate-50 p-3 rounded-xl border border-slate-100 flex flex-col items-center justify-center">
                  <p className="text-[10px] text-gray-400 font-bold uppercase mb-1">Generated OTP Code</p>
                  <p className="text-2xl font-black tracking-[0.2em] text-gray-800">{surgery.activeOTP.code}</p>
                </div>

                <div className="flex items-center gap-2">
                  <input
                    type="text"
                    maxLength={6}
                    placeholder="Enter OTP"
                    value={otpInputs[surgery._id] || ''}
                    onChange={(e) => setOtpInputs(prev => ({ ...prev, [surgery._id]: e.target.value.replace(/\D/g, '') }))}
                    className="w-full min-w-0 bg-gray-50 border border-gray-200 rounded-xl px-4 py-3 text-sm font-bold focus:outline-none focus:border-amber-400 focus:ring-2 focus:ring-amber-400/20 text-center tracking-widest"
                  />
                  <button
                    onClick={() => handleVerifyOtp(surgery._id, surgery.activeOTP._id, surgery.activeOTP.purpose)}
                    disabled={verifyingOtp || (otpInputs[surgery._id]?.length !== 6)}
                    className="bg-amber-500 hover:bg-amber-600 disabled:opacity-50 disabled:cursor-not-allowed text-white px-6 py-3 rounded-xl text-sm font-black uppercase transition-colors shrink-0"
                  >
                    Verify
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Profile Quick Stats */}
      <div className="flex flex-col md:flex-row items-center justify-between gap-6 bg-white p-6 rounded-[32px] border border-gray-100 shadow-sm">
        <div className="flex items-center gap-4">
          <div className="bg-blue-50 p-4 rounded-2xl">
            <Stethoscope className="text-blue-600 w-8 h-8" />
          </div>
          <div>
            <p className="text-gray-400 text-[10px] font-black uppercase tracking-widest">Specialization</p>
            <h4 className="text-xl font-black text-[#143E78]">
              {doctor.specializationBranch
                ? `${doctor.specializationBranch} (${doctor.specialization})`
                : `${doctor.specialization || 'Clinical'} Specialist`}
            </h4>
          </div>
        </div>
        <div className="flex items-center gap-4 bg-slate-50 p-4 rounded-2xl border border-slate-100 w-full md:w-auto lg:w-72">
          <div className="bg-white p-3 rounded-xl shadow-sm">
            <Wallet className="w-6 h-6 text-blue-600" />
          </div>
          <div>
            <p className="text-[10px] font-black text-gray-400 uppercase">Earnings</p>
            <p className="text-xl font-black text-gray-800 tracking-tighter">₹{doctor.totalEarnings?.toLocaleString() || '0'}</p>
          </div>
        </div>
      </div>

      {/* High-Level Stats Grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
        {[
          { label: 'Total Surgeries', value: doctor.totalSurgeries, icon: Stethoscope, color: 'text-blue-600', bg: 'bg-blue-50' },
          { label: 'Assigned Leads', value: data.assignedLeads, icon: Users, color: 'text-indigo-600', bg: 'bg-indigo-50' },
          { label: 'Active Cases', value: surgeryStats?.in_progress || 0, icon: Activity, color: 'text-amber-600', bg: 'bg-amber-50' },
          { label: 'Success Rate', value: '98.4%', icon: CheckCircle2, color: 'text-emerald-600', bg: 'bg-emerald-50' },
        ].map((stat, i) => (
          <div key={i} className="bg-white p-5 sm:p-6 rounded-[32px] border border-gray-100 shadow-lg shadow-gray-200/40 group hover:scale-[1.02] transition-all">
            <div className={`w-10 h-10 sm:w-12 sm:h-12 ${stat.bg} ${stat.color} rounded-2xl flex items-center justify-center mb-4`}>
              <stat.icon className="w-5 h-5 sm:w-6 sm:h-6" />
            </div>
            <p className="text-[10px] font-black text-gray-400 uppercase tracking-wider mb-1">{stat.label}</p>
            <h4 className="text-2xl sm:text-3xl font-black text-[#143E78] tracking-tighter">{stat.value}</h4>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">

        {/* Left Column: Surgical Runway */}
        <div className="lg:col-span-8 space-y-8">
          <div className="bg-white rounded-[32px] sm:rounded-[40px] p-5 sm:p-8 shadow-xl shadow-gray-200/30 border border-gray-100">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
              <h3 className="text-lg sm:text-xl font-black text-[#143E78] flex items-center gap-3">
                <Calendar className="w-6 h-6 text-blue-600" /> Upcoming Surgical Pipeline
              </h3>
              <Link to="/surgeries" className="text-xs font-black text-blue-600 flex items-center gap-1 hover:underline">Full Schedule <ChevronRight className="w-3 h-3" /></Link>
            </div>

            <div className="space-y-4">
              {upcomingSurgeries.length > 0 ? upcomingSurgeries.map(surgery => (
                <div key={surgery._id} className="p-4 sm:p-5 bg-gray-50/50 rounded-[28px] border border-gray-50 flex flex-col md:flex-row items-center justify-between gap-4 hover:bg-white hover:border-blue-100 transition-all group">
                  <div className="flex items-center gap-4 sm:gap-5 w-full">
                    <div className="w-12 h-12 sm:w-14 sm:h-14 bg-white rounded-2xl shadow-sm border border-gray-100 flex flex-col items-center justify-center flex-shrink-0">
                      <span className="text-[9px] sm:text-[10px] font-black text-blue-600 uppercase tracking-tighter">{new Date(surgery.scheduledDate).toLocaleString('default', { month: 'short' })}</span>
                      <span className="text-lg sm:text-xl font-black text-[#143E78] leading-none">{new Date(surgery.scheduledDate).getDate()}</span>
                    </div>
                    <div className="min-w-0 flex-1">
                      <h5 className="font-black text-gray-800 group-hover:text-blue-600 transition-colors uppercase italic tracking-tighter truncate text-sm sm:text-base">{surgery.surgeryName}</h5>
                      <div className="flex flex-wrap items-center gap-2 mt-1 text-[10px] font-bold text-gray-400">
                        <span className="flex items-center gap-1 whitespace-nowrap"><Clock className="w-3 h-3" /> {surgery.scheduledTime}</span>
                        <span className="hidden xs:inline w-1 h-1 bg-gray-200 rounded-full"></span>
                        <span className="truncate">{surgery.hospital?.hospitalName || 'HOSPITAL PENDING'}</span>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-4 w-full md:w-auto justify-between md:justify-end border-t md:border-none pt-3 md:pt-0">
                    <span className={`px-4 py-1.5 rounded-full text-[9px] font-black uppercase tracking-widest ${surgery.status === 'scheduled' ? 'bg-blue-100 text-blue-700' : 'bg-amber-100 text-amber-700'}`}>
                      {surgery.status}
                    </span>
                    <Link to={`/surgeries/${surgery._id}`} className="p-2 sm:p-3 bg-white hover:bg-blue-600 hover:text-white rounded-xl sm:rounded-2xl shadow-sm border border-gray-100 transition-all">
                      <ChevronRight className="w-4 h-4 sm:w-5 sm:h-5" />
                    </Link>
                  </div>
                </div>
              )) : (
                <div className="py-20 text-center">
                  <div className="bg-gray-50 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Activity className="w-8 h-8 text-gray-200" />
                  </div>
                  <p className="text-sm font-bold text-gray-400">Your surgical pipeline is currently clear.</p>
                </div>
              )}
            </div>
          </div>

          {/* Recent Clinical Reviews */}
          <div className="bg-white rounded-[32px] sm:rounded-[40px] p-5 sm:p-8 shadow-xl shadow-gray-200/30 border border-gray-100">
            <div className="flex items-center justify-between gap-2 mb-6 sm:mb-8">
              <h3 className="text-base sm:text-xl font-black text-[#143E78] flex items-center gap-2 sm:gap-3">
                <Stethoscope className="w-5 h-5 sm:w-6 sm:h-6 text-emerald-600" /> <span className="hidden xs:inline">Recent</span> Clinical Reviews
              </h3>
              {recentReviews && recentReviews.length > 3 && (
                <button
                  onClick={() => setShowAllReviews(!showAllReviews)}
                  className="text-[10px] sm:text-xs font-black text-emerald-600 flex items-center gap-1 hover:underline whitespace-nowrap"
                >
                  {showAllReviews ? (
                    <>Show Less</>
                  ) : (
                    <>View All ({recentReviews.length}) <ChevronRight className="w-3 h-3" /></>
                  )}
                </button>
              )}
            </div>

            <div className="space-y-4">
              {recentReviews && recentReviews.length > 0 ? (showAllReviews ? recentReviews : recentReviews.slice(0, 3)).map(review => (
                <div key={review._id} className="p-4 sm:p-5 bg-gray-50/50 rounded-[24px] sm:rounded-[28px] border border-gray-50 flex flex-col lg:flex-row lg:items-center justify-between gap-3 sm:gap-4 transition-all group">
                  <div className="flex items-center gap-3 sm:gap-5 w-full">
                    <div className={`w-12 h-12 sm:w-14 sm:h-14 rounded-2xl shadow-sm border flex flex-col items-center justify-center flex-shrink-0 ${review.clinicalData?.reviewDecision === 'Surgery Possible' ? 'bg-emerald-50 border-emerald-100' : review.clinicalData?.reviewDecision === 'Additional Investigation Required' ? 'bg-amber-50 border-amber-100' : 'bg-rose-50 border-rose-100'}`}>
                      {review.clinicalData?.reviewDecision === 'Surgery Possible' ? <CheckCircle2 className="w-5 h-5 sm:w-6 sm:h-6 text-emerald-500" /> : <Activity className={`w-5 h-5 sm:w-6 sm:h-6 ${review.clinicalData?.reviewDecision === 'Additional Investigation Required' ? 'text-amber-500' : 'text-rose-500'}`} />}
                    </div>
                    <div className="min-w-0 flex-1">
                      <h5 className="font-black text-gray-800 uppercase tracking-tighter truncate text-sm sm:text-base">{review.patientName || 'Unknown Patient'}</h5>
                      <div className="flex flex-wrap items-center gap-1.5 sm:gap-2 mt-1 text-[9px] sm:text-[10px] font-bold text-gray-400">
                        <span className="truncate">{review.disease || 'General Consult'}</span>
                        <span className="hidden xs:inline w-1 h-1 bg-gray-200 rounded-full"></span>
                        <span className="truncate flex items-center gap-1"><Clock className="w-3 h-3" /> {new Date(review.clinicalData?.reviewSubmittedAt || review.updatedAt || review.createdAt || Date.now()).toLocaleDateString()}</span>
                      </div>
                      {review.clinicalData?.reviewRemarks ? (
                        <p className="mt-1 sm:mt-2 text-[10px] sm:text-xs text-slate-500 italic truncate max-w-[200px] xs:max-w-[250px] sm:max-w-sm">"{review.clinicalData.reviewRemarks}"</p>
                      ) : (
                        <p className="mt-1 sm:mt-2 text-[10px] sm:text-xs text-slate-400 italic truncate max-w-[200px] xs:max-w-[250px] sm:max-w-sm">No remarks provided</p>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center w-full lg:w-auto justify-center lg:justify-end border-t lg:border-none pt-3 lg:pt-0 mt-1 lg:mt-0">
                    {review.clinicalData?.reviewDecision ? (
                      <span className={`w-full lg:w-auto text-center px-3 py-2 sm:px-4 sm:py-1.5 rounded-xl sm:rounded-full text-[9px] font-black uppercase tracking-widest ${review.clinicalData?.reviewDecision === 'Surgery Possible' ? 'bg-emerald-100 text-emerald-700' : review.clinicalData?.reviewDecision === 'Additional Investigation Required' ? 'bg-amber-100 text-amber-700' : 'bg-rose-100 text-rose-700'}`}>
                        {review.clinicalData?.reviewDecision}
                      </span>
                    ) : (
                      <span className="w-full lg:w-auto text-center px-3 py-2 sm:px-4 sm:py-1.5 rounded-xl sm:rounded-full text-[9px] font-black uppercase tracking-widest bg-gray-100 text-gray-500">
                        REVIEW PENDING
                      </span>
                    )}
                  </div>
                </div>
              )) : (
                <div className="py-12 text-center">
                  <div className="bg-gray-50 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Stethoscope className="w-6 h-6 text-gray-200" />
                  </div>
                  <p className="text-sm font-bold text-gray-400">No recent clinical reviews.</p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Right Column: Contextual Widgets */}
        <div className="lg:col-span-4 space-y-8">

          {/* Clinical Identity Widget */}
          <div className="bg-white rounded-[32px] sm:rounded-[36px] p-6 sm:p-8 border border-gray-100 shadow-xl shadow-gray-200/30">
            <h3 className="text-lg font-black text-[#143E78] mb-6 flex items-center gap-3">
              <Stethoscope className="w-6 h-6 text-blue-500" /> Clinical Identity
            </h3>
            <div className="space-y-6">
              <div className="flex flex-col xs:flex-row justify-between gap-4 bg-blue-50/50 p-4 rounded-2xl border border-blue-100/50">
                <div>
                  <p className="text-[10px] font-black text-blue-400 uppercase">License Reg #</p>
                  <p className="text-sm font-black text-[#143E78]">{doctor.registrationNumber}</p>
                </div>
                <div className="xs:text-right">
                  <p className="text-[10px] font-black text-blue-400 uppercase">Primary Degree</p>
                  <p className="text-sm font-black text-[#143E78]">{doctor.primaryQualification}</p>
                </div>
              </div>

              {doctor.proceduralExpertise && (
                <div className="space-y-3">
                  <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest pl-1">Procedural Proficiency</p>
                  <div className="flex flex-wrap gap-2">
                    {Object.entries(doctor.proceduralExpertise)
                      .filter(([_, val]) => val)
                      .map(([key, _]) => (
                        <span key={key} className="px-3 py-1 bg-indigo-50 text-indigo-600 rounded-full text-[9px] font-black uppercase border border-indigo-100">
                          {key.replace(/([A-Z])/g, ' $1').trim()}
                        </span>
                      ))
                    }
                  </div>
                </div>
              )}

              <div className="pt-4 border-t border-gray-50 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <ShieldCheck className="w-4 h-4 text-emerald-500" />
                  <span className="text-[9px] font-black text-gray-400 uppercase">PI Insurance {doctor.insurance?.hasInsurance ? 'Active' : 'Missing'}</span>
                </div>
                <span className="text-[10px] font-black text-blue-600 uppercase italic">View Policy</span>
              </div>
            </div>
          </div>

          {/* Quick Action: Availability */}
          <div className="bg-gradient-to-br from-blue-600 to-indigo-700 rounded-[32px] sm:rounded-[36px] p-6 sm:p-8 text-white relative overflow-hidden shadow-2xl shadow-blue-900/20">
            <div className="relative z-10">
              <div className="flex items-center gap-2 mb-6">
                <div className="bg-white/20 p-2 rounded-lg backdrop-blur-md"><Zap className="w-5 h-5 text-white" /></div>
                <span className="text-[10px] font-black uppercase tracking-[0.2em] text-blue-100">Live Availability</span>
              </div>
              <h4 className="text-2xl font-black mb-2">{doctor.availabilityModel} Roster</h4>
              <p className="text-blue-100 text-xs mb-8 font-medium">Notice Preference: {doctor.noticePreference}</p>

              <Link to="/doctor-availability" className="w-full bg-white text-blue-600 py-4 rounded-2xl font-black text-sm text-center block shadow-lg shadow-blue-900/40 hover:bg-blue-50 active:scale-95 transition-all">
                Modify Availability
              </Link>
            </div>
            <div className="absolute top-0 right-0 w-48 h-48 bg-white/5 rounded-full -mr-16 -mt-16 blur-2xl"></div>
          </div>

          {/* Verification Alert Card */}
          <div className="bg-white rounded-[32px] sm:rounded-[36px] p-6 sm:p-8 border border-gray-100 shadow-xl shadow-gray-200/30">
            <h3 className="text-lg font-black text-[#143E78] mb-6 flex items-center gap-3">
              <Activity className="w-6 h-6 text-emerald-500" /> System Monitoring
            </h3>
            <div className="space-y-4">
              <div className="flex items-start gap-3 p-4 bg-emerald-50 rounded-2xl border border-emerald-100">
                <div className="mt-1"><CheckCircle2 className="w-4 h-4 text-emerald-600" /></div>
                <p className="text-xs font-bold text-emerald-800 leading-relaxed">No pending clinical alerts for your verified surgeries.</p>
              </div>
            </div>
          </div>

          {/* Weekly Availability Overview (Slider Window) */}
          <div className="bg-white rounded-[32px] sm:rounded-[36px] p-6 sm:p-8 border border-gray-100 shadow-xl shadow-gray-200/30 overflow-hidden relative group">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <div className="bg-indigo-50 p-2 rounded-xl"><Clock className="w-5 h-5 text-indigo-500" /></div>
                <h3 className="text-lg font-black text-[#143E78] tracking-tight uppercase">Weekly Roster</h3>
              </div>
              <Link to="/doctor-availability" className="text-[10px] font-black text-indigo-600 uppercase hover:underline flex items-center gap-1">
                Edit <ChevronRight className="w-3 h-3" />
              </Link>
            </div>

            <div className="relative">
              <div className="flex gap-4 overflow-x-auto pb-4 snap-x snap-mandatory no-scrollbar scroll-smooth">
                {['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'].map((fullDay) => {
                  const dayData = doctor.weeklySchedule?.find(s => s.day === fullDay);
                  const isAvailable = dayData?.isAvailable;

                  const formatTime = (timeStr) => {
                    if (!timeStr) return '';
                    const [hours, minutes] = timeStr.split(':');
                    let h = parseInt(hours);
                    const ampm = h >= 12 ? 'PM' : 'AM';
                    h = h % 12;
                    h = h ? h : 12;
                    return `${h}:${minutes} ${ampm}`;
                  };

                  return (
                    <div
                      key={fullDay}
                      className={`min-w-[150px] sm:min-w-[180px] snap-center p-6 rounded-3xl border transition-all duration-300 ${isAvailable
                        ? 'bg-gradient-to-br from-indigo-50/50 to-white border-indigo-100 shadow-sm ring-1 ring-indigo-50 hover:shadow-lg'
                        : 'bg-gray-50 border-gray-100 opacity-40 grayscale'
                        }`}
                    >
                      <p className="text-[10px] font-black uppercase text-slate-400 tracking-[0.2em] mb-4">{fullDay.substring(0, 3)}</p>
                      {isAvailable && dayData.timeSlots?.[0] ? (
                        <div className="space-y-4">
                          <div className="flex items-center gap-2">
                            <div className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse shadow-[0_0_8px_rgba(16,185,129,0.5)]"></div>
                            <span className="text-[10px] font-black text-emerald-600 uppercase tracking-widest">Active</span>
                          </div>
                          <div className="space-y-2">
                            <div>
                              <p className="text-[9px] font-bold text-slate-400 uppercase tracking-tighter">Start</p>
                              <p className="text-sm font-black text-[#143E78] tracking-tight">{formatTime(dayData.timeSlots[0].startTime)}</p>
                            </div>
                            <div>
                              <p className="text-[9px] font-bold text-slate-400 uppercase tracking-tighter">End</p>
                              <p className="text-sm font-black text-[#143E78] tracking-tight">{formatTime(dayData.timeSlots[0].endTime)}</p>
                            </div>
                          </div>
                        </div>
                      ) : (
                        <div className="py-10 flex flex-col items-center justify-center">
                          <span className="text-xs font-black text-slate-300 uppercase tracking-widest italic">Offline</span>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              {/* Edge Fade */}
              <div className="absolute right-0 top-0 w-16 h-full bg-gradient-to-l from-white to-transparent pointer-events-none group-hover:opacity-0 transition-opacity"></div>
            </div>

            <div className="mt-4 pt-6 border-t border-gray-50 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="flex -space-x-1">
                  {[1, 2, 3].map(i => <div key={i} className="w-2 h-2 rounded-full bg-indigo-200 border border-white"></div>)}
                </div>
                <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Notice: <span className="text-indigo-600 font-black">{doctor.noticePreference}</span></p>
              </div>
              <div className="flex items-center gap-2 text-[10px] font-black text-slate-300 uppercase tracking-tight animate-pulse">
                <span>Slide to View Week</span>
                <ChevronRight size={12} />
              </div>
            </div>
          </div>

          {/* Doxez Operations Support Card */}
          <div className="bg-gradient-to-br from-slate-900 to-slate-800 rounded-[32px] p-6 text-white border border-slate-700 shadow-xl relative overflow-hidden group">
            <div className="absolute right-0 bottom-0 top-0 w-1/3 bg-blue-500/10 rounded-l-full blur-2xl pointer-events-none group-hover:bg-blue-500/20 transition-all duration-300"></div>
            <div className="relative z-10 space-y-4">
              <div className="flex items-center gap-2">
                <div className="bg-blue-500/10 p-2 rounded-xl border border-blue-500/20"><PhoneCall className="w-5 h-5 text-blue-400" /></div>
                <h3 className="text-sm font-black uppercase tracking-wider text-blue-200">Doxez Support Desk</h3>
              </div>
              <p className="text-[11px] font-bold text-slate-300 leading-relaxed">
                Direct operations support is active for all cases, OTP verifications, and payouts.
              </p>
              <div className="flex items-center gap-2 pt-2">
                <button
                  onClick={() => {
                    const text = encodeURIComponent(`Hello Doxez Support Desk, I am Dr. ${user?.firstName || ''} ${user?.lastName || ''}. I need assistance.`);
                    window.open(`https://wa.me/917735970021?text=${text}`, '_blank');
                  }}
                  className="flex-1 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl text-[10px] font-black uppercase tracking-wider text-center transition-all"
                >
                  WhatsApp
                </button>
                <Link
                  to="/support"
                  className="flex-1 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-xl text-[10px] font-black uppercase tracking-wider text-center transition-all"
                >
                  Operations Hub
                </Link>
              </div>
            </div>
          </div>

        </div>

      </div>

    </div>
  );
};

export default DoctorDashboard;